// client/src/components/reports/ExportBar.jsx
//
// CSV / PDF export buttons. An export is queued on the server and built by
// the report-worker; this shows its progress and the download link when it's
// ready (the user also gets a push notification).

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileCsv, FaFilePdf } from 'react-icons/fa';
import useReportRun from './useReportRun';
import { runDownloadUrl, errorText } from '../../services/reportsApi';

export default function ExportBar({ onExport, disabled }) {
  const { t } = useTranslation();
  const [runId, setRunId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { run } = useReportRun(runId);

  const start = async (format) => {
    setBusy(true);
    setError('');
    setRunId(null);
    try {
      const res = await onExport(format);
      setRunId(res.runId);
    } catch (err) {
      setError(errorText(err, t('reports.export.error', 'Could not start the export.')));
    } finally {
      setBusy(false);
    }
  };

  const pending = runId && (!run || run.status === 'queued' || run.status === 'running');

  return (
    <div className="rp-actions" aria-live="polite">
      <button type="button" className="lm-btn secondary" disabled={disabled || busy || pending} onClick={() => start('csv')}>
        <FaFileCsv aria-hidden="true" /> {t('reports.export.csv', 'Export CSV')}
      </button>
      <button type="button" className="lm-btn secondary" disabled={disabled || busy || pending} onClick={() => start('pdf')}>
        <FaFilePdf aria-hidden="true" /> {t('reports.export.pdf', 'Export PDF')}
      </button>
      {pending && <span className="rp-status">{t('reports.export.preparing', 'Preparing your file… you can leave this page; we\'ll notify you.')}</span>}
      {run && run.status === 'done' && (
        <a className="lm-btn" href={runDownloadUrl(run.id)}>
          {t('reports.export.download', 'Download {{format}}', { format: run.format.toUpperCase() })}
          {run.rowCount != null && ` (${t('reports.export.rows', '{{count}} rows', { count: run.rowCount })})`}
        </a>
      )}
      {run && run.status === 'failed' && <span className="note error">{run.error || t('reports.export.failed', 'The export failed.')}</span>}
      {error && <span className="note error" role="alert">{error}</span>}
    </div>
  );
}
